import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { signIn, sendPasswordResetEmail } from '../hooks/useSafeAuth';
import versionData from '../../version.json';
import { useAuthCache } from '../contexts/AuthCacheContext';

interface LoginForm {
  email: string;
  password: string;
}

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors }, getValues } = useForm<LoginForm>();
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const { refreshUser } = useAuthCache();

  // Get the redirect path from location state, default to overview
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/overview';

  const onSubmit = async (data: LoginForm) => {
    try {
      const { error } = await signIn(data.email, data.password);
      if (error) {
        toast.error(error.message);
        console.error('Login error:', error);
      } else {
        // Ensure auth state is populated prior to routing to protected pages
        try { await refreshUser(); } catch {}
        toast.success('Successfully logged in!');
        navigate(from || '/overview', { replace: true });
      }
    } catch (err) {
      console.error('Login exception:', err);
      toast.error('An unexpected error occurred during login');
    }
  };

  const handleForgotPassword = async () => {
    const email = getValues('email');
    
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }

    setIsSubmittingReset(true);
    try {
      await sendPasswordResetEmail(email);
      
      // For local auth not implemented; this will throw
      if (false) {
        // Use setTimeout to ensure toast is displayed
        setTimeout(() => {
          toast.success('Password reset email sent! Check your inbox.', {
            duration: 5000,
            important: true,
          });
        }, 100);
        
        // Also show an alert as backup
        alert('Password reset email sent! Please check your inbox for further instructions.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      toast.error('Password reset not available in local auth');
    } finally {
      setIsSubmittingReset(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
        Welcome back
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            {...register('email', { required: 'Email is required' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isSubmittingReset}
              className="text-sm text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
            >
              {isSubmittingReset ? 'Sending...' : 'Forgot password?'}
            </button>
          </div>
          <input
            type="password"
            id="password"
            {...register('password', { required: 'Password is required' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Sign in
        </button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-400">v{versionData.version}</p>
      </div>
    </div>
  );
}

export default LoginPage;
